"use client"

// Globaler Error-Boundary. In Production gibt Next.js bei SERVER-Fehlern nur
// `error.digest` (Message redacted) — daher prüfen wir für Server-Action-/Build-
// Drift aktiv den Build-Hash gegen den initial gesehenen (BuildHeartbeat).
//
// CLIENT-Fehler (ChunkLoadError / fehlgeschlagener dynamischer Import) tragen
// dagegen ihren echten Namen/Message — die erkennen wir DIREKT. Sie entstehen
// typisch nach einem Deploy (Browser hat altes Bundle, Server liefert neu
// gehashte Chunks). Ein Soft-`reset()` hilft dabei NIE — nur ein Voll-Reload
// holt das neue Bundle. Deshalb: Chunk-Fehler → Auto-Voll-Reload (mit Loop-
// Schutz), und der „Erneut versuchen"-Button macht ebenfalls einen Voll-Reload.

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { RefreshCw, ArrowLeft } from "lucide-react"

const INITIAL_BUILD_KEY = "rvs.initial_build_id"
const AUTORELOAD_TS_KEY = "rvs.autoreload_ts"

// Chunk-/Bundle-Fehler erkennen (Client-seitig, Name/Message nicht redacted).
function isChunkError(err?: { name?: string; message?: string }): boolean {
  const s = `${err?.name ?? ""} ${err?.message ?? ""}`
  return /ChunkLoadError|Loading chunk\s|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|'text\/html' is not a valid JavaScript MIME/i.test(
    s,
  )
}

// Loop-Schutz: nach einem Auto-Reload nicht sofort erneut reloaden.
function recentlyAutoReloaded(): boolean {
  try {
    const t = Number(sessionStorage.getItem(AUTORELOAD_TS_KEY) || "0")
    return Date.now() - t < 10_000
  } catch {
    return false
  }
}
function hardReload() {
  try {
    sessionStorage.setItem(AUTORELOAD_TS_KEY, String(Date.now()))
    sessionStorage.removeItem(INITIAL_BUILD_KEY)
  } catch {
    // ignore
  }
  window.location.reload()
}

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [diagnosis, setDiagnosis] = useState<
    "unknown" | "build_drift" | "real_bug"
  >("unknown")

  useEffect(() => {
    let cancelled = false
    async function diagnose() {
      // 1. Chunk-/Bundle-Fehler direkt am Error erkennen (häufigster Deploy-Fall).
      //    Soft-Reset hilft nie → als Drift behandeln + Voll-Reload. Loop-Schutz:
      //    wenn wir gerade eben schon reloaded haben und es kommt WIEDER, ist es
      //    kein transienter Drift → echter Bug anzeigen statt endlos reloaden.
      if (isChunkError(error)) {
        if (!cancelled) setDiagnosis(recentlyAutoReloaded() ? "real_bug" : "build_drift")
        return
      }

      // 2. Server-Fehler (redacted): Build-Hash-Drift aktiv prüfen.
      let stored: string | null = null
      try {
        stored = sessionStorage.getItem(INITIAL_BUILD_KEY)
      } catch {
        // ignore
      }
      if (!stored) {
        if (!cancelled) setDiagnosis("real_bug")
        return
      }
      try {
        const res = await fetch("/api/build-info", {
          cache: "no-store",
          credentials: "include",
        })
        if (!res.ok) {
          if (!cancelled) setDiagnosis("real_bug")
          return
        }
        const data = (await res.json()) as { build_id?: string }
        if (cancelled) return
        if (data.build_id && data.build_id !== stored && !recentlyAutoReloaded()) {
          setDiagnosis("build_drift")
        } else {
          setDiagnosis("real_bug")
        }
      } catch {
        if (!cancelled) setDiagnosis("real_bug")
      }
    }
    diagnose()
    return () => {
      cancelled = true
    }
  }, [error])

  useEffect(() => {
    if (diagnosis !== "build_drift") return
    const t = setTimeout(() => hardReload(), 1500)
    return () => clearTimeout(t)
  }, [diagnosis])

  return (
    <html lang="de">
      <body>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full rounded-lg border bg-card p-6 space-y-4 text-center">
            <div className="text-4xl">
              {diagnosis === "build_drift" ? "🔄" : "⚠️"}
            </div>
            <h1 className="text-xl font-semibold">
              {diagnosis === "build_drift"
                ? "System wurde gerade aktualisiert"
                : diagnosis === "unknown"
                  ? "Lade Fehler-Diagnose…"
                  : "Etwas ist schiefgelaufen"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {diagnosis === "build_drift"
                ? "Die Seite wird gleich neu geladen, damit du die neueste Version nutzen kannst."
                : diagnosis === "unknown"
                  ? "Prüfe gerade ob das System gerade ein Update gemacht hat…"
                  : "Bitte die Seite neu laden. Falls das Problem bestehen bleibt, ist es kurzzeitig ein Server-Problem."}
            </p>
            {error?.digest && diagnosis !== "build_drift" && (
              <p className="text-xs text-muted-foreground font-mono">
                Fehler-ID: {error.digest}
              </p>
            )}
            {diagnosis !== "unknown" && (
              <div className="flex items-center justify-center gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.history.back()}
                >
                  <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
                  Zurück
                </Button>
                {/* Immer Voll-Reload: holt bei Chunk-/Build-Drift das neue Bundle
                    (Soft-reset() würde im selben kaputten Client hängen bleiben). */}
                <Button size="sm" onClick={hardReload}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  {diagnosis === "build_drift"
                    ? "Jetzt neu laden"
                    : "Erneut versuchen"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </body>
    </html>
  )
}
